import { useState } from "react"
import { IconType } from "react-icons"
import { BiSolidDashboard } from "react-icons/bi"
import { FaBullhorn, FaFire, FaMoneyBill, FaUsers, FaChartBar, FaChartPie, FaGear, FaFileInvoiceDollar, FaFlask, FaBinoculars, FaClockRotateLeft } from "react-icons/fa6"
import { useLocation, useNavigate } from "react-router-dom"

import { Drawer, HStack, Icon, Portal, Spacer, Stack } from "@chakra-ui/react"
import { DEAL_MANAGER_NAV_OPTIONS } from "@/constants"
import { NavOption } from "@/types"
import { Text } from "../ui"

const getIconForLabel = (label: string): IconType => {
  switch (label) {
    case NavOption.DASHBOARD:
      return BiSolidDashboard
    case NavOption.PIPELINE:
      return FaUsers
    case NavOption.HOTLIST:
      return FaFire
    case NavOption.FMV_REVIEW:
      return FaFileInvoiceDollar
    case NavOption.IRR:
      return FaMoneyBill
    case NavOption.EMPLOYEE_OUTREACH:
      return FaBullhorn
    case NavOption.PORTAL_EXTRACTION:
      return FaFlask
    case NavOption.STRANDED_VALUE:
      return FaBinoculars
    case NavOption.COMMISSIONS:
      return FaChartBar
    case NavOption.REPORTS:
      return FaChartPie
    case NavOption.ACTIVITY:
      return FaClockRotateLeft
    case NavOption.SETTINGS:
      return FaGear
    default:
      return FaUsers
  }
}

const convertUnderscoresToSpaces = (str: string) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

const NavItemIcon = ({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) => {
  return (
    <Stack
      p={3}
      alignItems="center"
      backgroundColor={isActive ? "whiteAlpha.100" : "transparent"}
      cursor="pointer"
      _hover={{ backgroundColor: "whiteAlpha.100" }}
      onClick={onClick}
    >
      <Icon as={getIconForLabel(label) as React.ElementType} size="sm" color={isActive ? "white" : "#B9BBBE"} />
    </Stack>
  )
}

const NavItemFull = ({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) => {
  return (
    <Stack
      p={3}
      backgroundColor={isActive ? "whiteAlpha.100" : "transparent"}
      cursor="pointer"
      _hover={{ backgroundColor: "whiteAlpha.100" }}
      onClick={onClick}
    >
      <HStack>
        <Icon as={getIconForLabel(label) as React.ElementType} size="sm" color={isActive ? "white" : "#B9BBBE"} />
        <Text fontSize="sm" fontWeight="medium" color={isActive ? "white" : "#B9BBBE"}>
          {label === NavOption.IRR ? "IRR" : label === NavOption.HOTLIST ? "Hotlist" : label === NavOption.FMV_REVIEW ? "FMV Review" : label === NavOption.EMPLOYEE_OUTREACH ? "Employee Outreach" : label === NavOption.PORTAL_EXTRACTION ? "Portal Extraction" : label === NavOption.STRANDED_VALUE ? "Stranded Value" : label === NavOption.REPORTS ? "Reports" : label === NavOption.COMMISSIONS ? "Commissions" : label === NavOption.SETTINGS ? "Settings" : convertUnderscoresToSpaces(label)}
        </Text>
      </HStack>
    </Stack>
  )
}

export const DealManagerNavigation = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const checkIsCurrentNavOption = (option: string) => {
    return location.pathname === `/core/${option}`
  }

  const handleNavigate = (label: string) => {
    navigate(`/core/${label}`)
    setIsDrawerOpen(false)
  }

  return (
    <>
      <Stack
        h="100%"
        bg="sidebar.bg"
        color="white"
        pb={10}
        alignItems="center"
        pt={3}
        onMouseEnter={() => setIsDrawerOpen(true)}
      >
        {DEAL_MANAGER_NAV_OPTIONS.map((option) => (
          <NavItemIcon
            key={option}
            label={option}
            isActive={checkIsCurrentNavOption(option)}
            onClick={() => handleNavigate(option)}
          />
        ))}
        <Spacer />
      </Stack>

      <Drawer.Root
        open={isDrawerOpen}
        onOpenChange={(e) => setIsDrawerOpen(e.open)}
        placement="start"
        size="xs"
      >
        <Portal>
          <Drawer.Backdrop bg="transparent" />
          <Drawer.Positioner>
            <Drawer.Content
              bg="sidebar.bg"
              color="white"
              onMouseLeave={() => setIsDrawerOpen(false)}
            >
              <Drawer.Body p={0}>
                <Stack h="100%" pb={10}>
                  <Text fontSize="xl" fontWeight="bold" pt={5} pb={5} pl={5}>
                    ESO Fund Client Portal
                  </Text>
                  {DEAL_MANAGER_NAV_OPTIONS.map((option) => (
                    <NavItemFull
                      key={option}
                      label={option}
                      isActive={checkIsCurrentNavOption(option)}
                      onClick={() => handleNavigate(option)}
                    />
                  ))}
                  <Spacer />
                </Stack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}
